// src/components/team-messages/FullPageThread.tsx
import {
  useState, useEffect, useRef, useCallback,
  type ChangeEvent, type KeyboardEvent as KE,
} from 'react';
import {
  Hash, Users, Paperclip, X, FileText,
  ArrowRight, User, Briefcase,
  ChevronDown, Image, Smile, AtSign,
  Send, MessageCircle, Plus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type {
  TeamConversation,
  TeamMessage,
  TeamMessageAttachment,
  OrgMember,
} from '@/types/teamMessages';

// ─── relative timestamp ───────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1)    return 'now';
    if (mins < 60)   return `${mins} min${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)    return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1)  return 'yesterday';
    if (days < 7)    return `${days} days ago`;
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatWallTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function getDateLabel(iso: string): string {
  try {
    const d     = new Date(iso);
    const today = new Date();
    const diff  = Math.floor(
      (today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000,
    );
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function groupByDate<T extends { createdAt: string }>(items: T[]): { label: string; items: T[] }[] {
  const groups: { label: string; items: T[] }[] = [];
  for (const item of items) {
    const label = getDateLabel(item.createdAt);
    const last  = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}

function getConvDisplayName(conv: TeamConversation, currentUserId: string): string {
  if (conv.type === 'channel' || conv.type === 'updates') return conv.name ?? '';
  if (conv.type === 'group') return conv.name ?? 'Group';
  const other = conv.members.find((m) => m.userId !== currentUserId);
  return other?.displayName ?? 'Direct Message';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// Highlight @mentions in message body
function renderBody(body: string): React.ReactNode {
  const parts = body.split(/(@\w[\w\s]*?\b)/g);
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <span
        key={i}
        className="inline-flex items-center rounded-sm px-1 py-0 text-[12px] font-medium bg-[#2e2e2e] text-white mx-0.5"
      >
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

const JOB_PATTERN = /\bJ-\d+\b/gi;

function extractJobRefs(body: string): string[] {
  return [...new Set(body.match(JOB_PATTERN) ?? [])];
}

// Parse "[Job JOB-005] message text" prefix — used by Ordering/Concerns job messages
function parseJobBody(body: string): { jobRef: string | null; text: string } {
  const match = body.match(/^\[Job\s+([^\]]+)\]\s*/i);
  if (match) return { jobRef: match[1].trim(), text: body.slice(match[0].length).trim() };
  return { jobRef: null, text: body };
}

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, '').trim();
}

// ─── local types ─────────────────────────────────────────────────────────────

interface AttachmentPreview {
  id: string;
  file: File;
  previewUrl: string | null;
  isImage: boolean;
}

type MentionType = 'user' | 'job';
interface MentionItem {
  type: MentionType;
  id: string;
  label: string;
  role?: string;
}

// ─── sub-components ──────────────────────────────────────────────────────────

function JobChips({ body }: { body: string }) {
  const refs = extractJobRefs(body);
  if (refs.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {refs.map((ref) => (
        <Link
          key={ref}
          to={`/jobs/${ref}`}
          className="inline-flex items-center gap-1 rounded border border-[#3a3a3a] bg-[#1e1e1e] text-[#fafafa] px-1.5 py-0.5 text-[10px] font-mono font-medium hover:bg-[#2e2e2e] transition-colors tabular-nums"
        >
          <ArrowRight className="h-2.5 w-2.5 shrink-0" />
          {ref}
        </Link>
      ))}
    </div>
  );
}

// Check whether two consecutive messages are from the same sender within 5 minutes
function isContinuation(prev: TeamMessage | null, curr: TeamMessage): boolean {
  if (!prev) return false;
  if (prev.senderId !== curr.senderId) return false;
  const diff = new Date(curr.createdAt).getTime() - new Date(prev.createdAt).getTime();
  return diff < 5 * 60_000;
}

function SlackMessage({
  message,
  prevMessage,
  isGroupOrChannel: _isGroupOrChannel,
}: {
  message: TeamMessage;
  prevMessage: TeamMessage | null;
  isGroupOrChannel: boolean;
}) {
  const continued = isContinuation(prevMessage, message);
  const isUrgent = /\[urgent\]/i.test(message.body);
  const bodyClean = message.body
    .replace(/\[urgent\]\s*/i, '')
    .replace(/<[^>]*>/g, '')
    .trim();

  // ── System message ─────────────────────────────────────────────────────────
  if (message.isSystem) {
    const meta   = message.metadata;
    const action = meta?.action ?? null;
    // SystemUpdateService bodies start with "System:" and have a concise meta.title.
    // job_message-bridge bodies are HTML (e.g. "<p>msg</p>") and meta.title is sender name.
    const isSystemUpdate = bodyClean.startsWith('System:');
    const msgText  = isSystemUpdate ? (meta?.title ?? bodyClean) : bodyClean;
    // Strip HTML from detail (job_message bridge sometimes stores HTML there)
    const rawDetail = meta?.detail ? stripHtml(meta.detail) : null;
    // Normalize "Job JOB-005" → "JOB-005" for the chip
    const jobChip = rawDetail
      ? (rawDetail.startsWith('Job ') ? rawDetail.slice(4).trim() : rawDetail)
      : null;
    return (
      <div className={cn('flex items-start gap-2 px-4', continued ? 'mt-0.5' : 'mt-3')}>
        <div className="w-8 shrink-0 pt-5">
          {!continued ? (
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold text-[#5a5a5a] bg-[#1e1e1e] border border-[#2e2e2e] shrink-0">
              SY
            </div>
          ) : (
            <div className="w-8" />
          )}
        </div>
        <div className="flex flex-col items-start max-w-[65%]">
          {!continued && (
            <span className="text-[11px] text-[#5a5a5a] font-medium mb-1 px-1">System</span>
          )}
          <div className={cn(
            'bg-[#141414] border border-[#2e2e2e] px-4 py-3 text-sm leading-snug',
            continued ? 'rounded-2xl' : 'rounded-2xl rounded-tl-sm',
          )}>
            <p className="text-[#fafafa] text-[13px] font-medium">{msgText}</p>
            {jobChip && (
              <span className="inline-flex items-center mt-2 rounded border border-[#3a3a3a] bg-[#1e1e1e] px-2 py-0.5 text-[11px] font-mono text-[#fafafa] tabular-nums">
                {jobChip}
              </span>
            )}
            {action && (
              <div className="mt-3">
                <Link
                  to={action.path}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-white text-black text-[11px] font-semibold px-3 py-1.5 hover:bg-[#e0e0e0] transition-colors"
                >
                  {action.label}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
          <span className="text-[10px] text-[#5a5a5a] tabular-nums mt-1 px-1">
            {formatWallTime(message.createdAt)}
          </span>
        </div>
      </div>
    );
  }

  // ── Own message (right) ────────────────────────────────────────────────────
  if (message.isOwn) {
    const { jobRef, text: msgText } = parseJobBody(bodyClean);
    const action = message.metadata?.action ?? null;
    return (
      <div className={cn('flex justify-end px-4', continued ? 'mt-0.5' : 'mt-3')}>
        <div className="flex flex-col items-end max-w-[65%]">
          <div
            className={cn(
              'bg-white text-black px-4 py-2.5 text-sm leading-[1.55] whitespace-pre-wrap',
              continued ? 'rounded-2xl' : 'rounded-2xl rounded-tr-sm',
            )}
          >
            {isUrgent && (
              <span className="inline-flex items-center rounded-sm px-1.5 py-0 text-[9px] font-bold bg-black/10 text-black uppercase tracking-wide mr-1.5 mb-1">
                Urgent
              </span>
            )}
            {msgText.split(/(@\w[\w\s]*?\b)/g).map((part, i) =>
              part.startsWith('@') ? (
                <span key={i} className="inline-flex items-center rounded-sm px-1 py-0 text-[12px] font-medium bg-black/10 text-black mx-0.5">
                  {part}
                </span>
              ) : (
                <span key={i}>{part}</span>
              ),
            )}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {message.attachments.map((a, i) =>
                  a.isImage && a.previewUrl ? (
                    <img key={i} src={a.previewUrl} alt={a.name} className="rounded-lg object-cover" style={{ maxHeight: 180, maxWidth: 280 }} />
                  ) : (
                    <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-black/10 bg-black/5 px-2 py-1 text-[10px] text-black/70">
                      <FileText className="h-2.5 w-2.5 shrink-0" />
                      <span className="truncate max-w-[120px]">{a.name}</span>
                    </span>
                  ),
                )}
              </div>
            )}
            {jobRef && (
              <div className="mt-2">
                <span className="inline-flex items-center rounded border border-black/15 bg-black/5 px-2 py-0.5 text-[11px] font-mono text-black tabular-nums">
                  {jobRef}
                </span>
              </div>
            )}
            {action && (
              <div className="mt-2 pt-2 border-t border-black/10">
                <Link
                  to={action.path}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-black text-white text-[11px] font-semibold px-3 py-1.5 hover:bg-black/80 transition-colors"
                >
                  {action.label}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
          <span className="text-[10px] text-[#5a5a5a] tabular-nums mt-1 px-1">
            {formatWallTime(message.createdAt)}
          </span>
        </div>
      </div>
    );
  }

  // ── Others' message (left) ─────────────────────────────────────────────────
  const { jobRef: otherJobRef, text: otherMsgText } = parseJobBody(bodyClean);
  const otherAction = message.metadata?.action ?? null;
  return (
    <div className={cn('flex items-start gap-2 px-4', continued ? 'mt-0.5' : 'mt-3')}>
      {/* Avatar column */}
      <div className="w-8 shrink-0 pt-5">
        {!continued ? (
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white bg-[#2e2e2e] shrink-0">
            {message.senderInitials || message.senderName?.slice(0, 2) || '??'}
          </div>
        ) : (
          <div className="w-8" />
        )}
      </div>

      {/* Bubble + meta */}
      <div className="flex flex-col items-start max-w-[65%]">
        {!continued && (
          <span className="text-[11px] text-[#5a5a5a] font-medium mb-1 px-1">
            {message.senderName}
          </span>
        )}
        <div
          className={cn(
            'bg-[#1a1a1a] text-[#fafafa] border border-[#2e2e2e] px-4 py-2.5 text-sm leading-[1.55] whitespace-pre-wrap',
            continued ? 'rounded-2xl' : 'rounded-2xl rounded-tl-sm',
          )}
        >
          {isUrgent && (
            <span className="inline-flex items-center rounded-sm px-1.5 py-0 text-[9px] font-bold bg-[#2e2e2e] text-white uppercase tracking-wide mr-1.5 mb-1">
              Urgent
            </span>
          )}
          {renderBody(otherMsgText)}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {message.attachments.map((a, i) =>
                a.isImage && a.previewUrl ? (
                  <img key={i} src={a.previewUrl} alt={a.name} className="rounded-lg border border-[#2E2E2E] object-cover" style={{ maxHeight: 180, maxWidth: 280 }} />
                ) : (
                  <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-[#2E2E2E] bg-[#1e1e1e] px-2 py-1 text-[10px] text-[#8A8A8A]">
                    <FileText className="h-2.5 w-2.5 shrink-0" />
                    <span className="truncate max-w-[120px]">{a.name}</span>
                  </span>
                ),
              )}
            </div>
          )}
          {otherJobRef && (
            <div className="mt-2">
              <span className="inline-flex items-center rounded border border-[#3a3a3a] bg-[#1e1e1e] px-2 py-0.5 text-[11px] font-mono text-[#fafafa] tabular-nums">
                {otherJobRef}
              </span>
            </div>
          )}
          {otherAction && (
            <div className="mt-2 pt-2 border-t border-[#2E2E2E]">
              <Link
                to={otherAction.path}
                className="inline-flex items-center gap-1.5 rounded-lg bg-white text-black text-[11px] font-semibold px-3 py-1.5 hover:bg-[#e0e0e0] transition-colors"
              >
                {otherAction.label}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </div>
        <span className="text-[10px] text-[#5a5a5a] tabular-nums mt-1 px-1">
          {formatWallTime(message.createdAt)}
        </span>
      </div>
    </div>
  );
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 my-4 px-4 select-none">
      <div className="flex-1 h-px bg-[#2e2e2e]" />
      <button className="flex items-center gap-1.5 rounded-full border border-[#2e2e2e] bg-[#1e1e1e] px-3 py-1 text-[10px] text-[#8a8a8a] hover:bg-[#2e2e2e] hover:text-[#fafafa] transition-colors">
        {label}
        <ChevronDown className="h-2.5 w-2.5" />
      </button>
      <div className="flex-1 h-px bg-[#2e2e2e]" />
    </div>
  );
}

function MentionDropdown({
  query,
  items,
  onSelect,
}: {
  query: string;
  items: MentionItem[];
  onSelect: (item: MentionItem) => void;
}) {
  const filtered = items.filter((m) => m.label.toLowerCase().includes(query.toLowerCase()));
  if (filtered.length === 0) return null;
  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 rounded-xl border border-[#2e2e2e] shadow-2xl z-10 overflow-hidden bg-[#161616]">
      {filtered.slice(0, 8).map((item) => (
        <button
          key={`${item.type}-${item.id}`}
          type="button"
          className="w-full flex items-center gap-2.5 text-xs px-3 py-2 hover:bg-[#1e1e1e] cursor-pointer text-left transition-colors text-[#fafafa]"
          onMouseDown={(e) => { e.preventDefault(); onSelect(item); }}
        >
          {item.type === 'user' ? (
            <User className="h-3.5 w-3.5 text-[#5a5a5a] shrink-0" />
          ) : (
            <Briefcase className="h-3.5 w-3.5 text-[#8a8a8a] shrink-0" />
          )}
          <span className={cn('flex-1', item.type === 'job' ? 'font-mono tabular-nums' : 'font-medium')}>
            {item.label}
          </span>
          {item.role && (
            <span className="text-[9px] text-[#5a5a5a]">{item.role}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────────────

interface ComposerProps {
  draft: string;
  onDraftChange: (v: string) => void;
  onSend: (body: string, attachments: AttachmentPreview[], mentionedUserIds: string[]) => Promise<void>;
  convType: 'dm' | 'group' | 'channel' | 'updates';
  mentionItems: MentionItem[];
}

const EMOJI_LIST = ['😊','👍','🎉','🔥','✅','❌','⚠️','📋','💬','🚀','💡','📌','👀','✏️','🔧','📦','💰','🏠','📅','⏰','🔔','📎','🖼️','🗂️'];

function Composer({ draft, onDraftChange, onSend, convType, mentionItems }: ComposerProps) {
  const textareaRef  = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [attachments, setAttachments]   = useState<AttachmentPreview[]>([]);
  const [showEmoji,   setShowEmoji]     = useState(false);

  useEffect(() => {
    return () => {
      attachments.forEach((a) => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detectMention = useCallback((value: string, cursorPos: number) => {
    const text   = value.slice(0, cursorPos);
    const lastAt = text.lastIndexOf('@');
    if (lastAt === -1) { setMentionQuery(null); return; }
    const afterAt = text.slice(lastAt + 1);
    if (afterAt.includes(' ')) { setMentionQuery(null); return; }
    setMentionQuery(afterAt);
  }, []);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onDraftChange(e.target.value);
    detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const handleMentionSelect = (item: MentionItem) => {
    const cursorPos = textareaRef.current?.selectionStart ?? draft.length;
    const lastAt    = draft.lastIndexOf('@', cursorPos);
    if (lastAt === -1) return;
    const insertion = `@${item.label}`;
    onDraftChange(`${draft.slice(0, lastAt)}${insertion} ${draft.slice(cursorPos)}`);
    setMentionQuery(null);
    setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const pos = lastAt + insertion.length + 1;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setAttachments((prev) => [
      ...prev,
      ...files.map((file) => {
        const isImage = file.type.startsWith('image/');
        return { id: crypto.randomUUID(), file, previewUrl: isImage ? URL.createObjectURL(file) : null, isImage };
      }),
    ]);
    e.target.value = '';
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => {
      const t = prev.find((a) => a.id === id);
      if (t?.previewUrl) URL.revokeObjectURL(t.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleSend = async () => {
    if (!draft.trim() && attachments.length === 0) return;
    // Resolve @DisplayName tokens to user IDs
    const mentionRegex = /@([\w][\w ]*?\b)/g;
    const mentionedUserIds: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = mentionRegex.exec(draft)) !== null) {
      const name = m[1].trim();
      const item = mentionItems.find(
        (i) => i.type === 'user' && i.label.toLowerCase() === name.toLowerCase(),
      );
      if (item && !mentionedUserIds.includes(item.id)) mentionedUserIds.push(item.id);
    }
    await onSend(draft.trim(), attachments, mentionedUserIds);
    setAttachments([]);
    setMentionQuery(null);
  };

  const handleKeyDown = (e: KE<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && mentionQuery !== null) { setMentionQuery(null); return; }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); }
  };

  const canSend = draft.trim().length > 0 || attachments.length > 0;

  const placeholder =
    convType === 'channel' ? 'Message the channel…' :
    convType === 'group'   ? 'Message the group…'   : 'Send a message…';

  return (
    <div className="px-4 pb-4 pt-2 shrink-0 bg-[#0a0a0a] border-t border-[#2e2e2e]">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Attachment preview strip */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2 px-3">
          {attachments.map((a) =>
            a.isImage && a.previewUrl ? (
              <div key={a.id} className="relative shrink-0">
                <img
                  src={a.previewUrl}
                  alt={a.file.name}
                  className="rounded-lg border border-[#2e2e2e] object-cover"
                  style={{ width: 48, height: 48 }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(a.id)}
                  className="absolute -top-1 -right-1 flex items-center justify-center h-4 w-4 rounded-full bg-[#161616] border border-[#2e2e2e] text-[#5a5a5a] hover:text-white"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ) : (
              <div
                key={a.id}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#2e2e2e] bg-[#1e1e1e] px-2 py-1 text-[10px]"
              >
                <FileText className="h-3 w-3 text-[#5a5a5a] shrink-0" />
                <span className="truncate max-w-[100px] text-[#fafafa]">{a.file.name}</span>
                <span className="text-[#5a5a5a] tabular-nums">{formatFileSize(a.file.size)}</span>
                <button type="button" onClick={() => handleRemoveAttachment(a.id)} className="ml-0.5 text-[#5a5a5a] hover:text-white">
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ),
          )}
        </div>
      )}

      {/* Composer box */}
      <div className="relative rounded-xl border border-[#2e2e2e] bg-[#1e1e1e]">
        {/* Mention dropdown */}
        {mentionQuery !== null && (
          <MentionDropdown query={mentionQuery} items={mentionItems} onSelect={handleMentionSelect} />
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={1}
          className="w-full min-h-[40px] max-h-36 text-[13px] resize-none leading-relaxed bg-transparent px-4 pt-3 pb-2 outline-none placeholder:text-[#5a5a5a] text-[#fafafa]"
        />

        {/* Emoji picker popover */}
        {showEmoji && (
          <div className="absolute bottom-full left-0 mb-1 z-20 bg-[#161616] border border-[#2e2e2e] rounded-[2px] p-2 grid grid-cols-6 gap-1">
            {EMOJI_LIST.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="h-7 w-7 flex items-center justify-center text-[16px] hover:bg-[#2e2e2e] rounded transition-colors"
                onClick={() => {
                  onDraftChange(draft + emoji);
                  setShowEmoji(false);
                  setTimeout(() => textareaRef.current?.focus(), 0);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Icon toolbar row */}
        <div className="flex items-center justify-between px-3 pb-2 pt-0">
          {/* Left icons */}
          <div className="flex items-center gap-0.5">
            <ComposerIconBtn icon={Plus} label="Add" onClick={() => fileInputRef.current?.click()} />
            <ComposerIconBtn icon={Image} label="Image" onClick={() => fileInputRef.current?.click()} />
            <ComposerIconBtn icon={Paperclip} label="Attach" onClick={() => fileInputRef.current?.click()} />
            <ComposerIconBtn
              icon={Smile}
              label="Emoji"
              onClick={() => setShowEmoji((v) => !v)}
            />
            <ComposerIconBtn
              icon={AtSign}
              label="Mention"
              onClick={() => {
                onDraftChange(draft + '@');
                setMentionQuery('');
                setTimeout(() => textareaRef.current?.focus(), 0);
              }}
            />
          </div>

          {/* Right: send */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => void handleSend()}
              disabled={!canSend}
              className={cn(
                'h-8 w-8 flex items-center justify-center rounded-lg transition-all',
                canSend
                  ? 'bg-white text-black hover:bg-[#e0e0e0] shadow-sm'
                  : 'bg-[#2e2e2e] text-[#5a5a5a] cursor-not-allowed',
              )}
              title="Send (Enter)"
            >
              <Send className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <p className="mt-1.5 text-[9px] text-[#5a5a5a] select-none px-1">
        Shift+Enter for new line &middot; @ to mention
      </p>
    </div>
  );
}

function ComposerIconBtn({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      className="h-7 w-7 flex items-center justify-center rounded-md text-[#5a5a5a] hover:text-white hover:bg-[#2e2e2e] transition-colors"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

interface FullPageThreadProps {
  conversation: TeamConversation | null;
  messages: TeamMessage[];
  loadingMessages: boolean;
  updatesChannelId: string | null;
  currentUserId: string;
  orgMembers: OrgMember[];
  canAddMembers: boolean;
  draft: string;
  onDraftChange: (v: string) => void;
  onSend: (body: string, attachments: TeamMessageAttachment[], mentionedUserIds: string[]) => Promise<void>;
  onAddMembers: () => void;
}

export function FullPageThread({
  conversation,
  messages,
  loadingMessages,
  updatesChannelId,
  currentUserId,
  orgMembers,
  canAddMembers,
  draft,
  onDraftChange,
  onSend,
  onAddMembers,
}: FullPageThreadProps) {
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, conversation?.id]);

  // Empty state
  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 select-none text-center bg-[#000000]">
        <div className="w-16 h-16 rounded-2xl bg-[#1e1e1e] flex items-center justify-center">
          <MessageCircle className="h-7 w-7 text-[#5a5a5a]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#8a8a8a]">Select a conversation</p>
          <p className="text-[11px] text-[#5a5a5a] mt-1">
            Choose a channel, group, or DM from the list
          </p>
        </div>
      </div>
    );
  }

  const isReadOnly  = conversation.id === updatesChannelId;
  const displayName = getConvDisplayName(conversation, currentUserId);
  const memberCount = conversation.members.length;
  const isGroup     = conversation.type === 'group';
  const isChannel   = conversation.type === 'channel' || conversation.type === 'updates';

  const mentionItems: MentionItem[] = orgMembers.map((m) => ({
    type: 'user' as const,
    id: m.userId,
    label: m.displayName,
    role: m.role,
  }));

  const handleSend = async (body: string, attachments: AttachmentPreview[], mentionedUserIds: string[]) => {
    await onSend(
      body,
      attachments.map((a) => ({
        name: a.file.name,
        isImage: a.isImage,
        previewUrl: a.previewUrl,
      })),
      mentionedUserIds,
    );
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full bg-[#000000]">
      {/* Thread header */}
      <div className="px-5 py-3 border-b border-[#2e2e2e] shrink-0 flex items-center justify-between bg-[#0a0a0a]">
        <div className="flex items-center gap-2.5">
          {/* Conv icon */}
          <div className={cn(
            'flex items-center justify-center shrink-0 text-[#5a5a5a]',
          )}>
            {isChannel ? (
              <Hash className="h-4 w-4" />
            ) : isGroup ? (
              <Users className="h-4 w-4" />
            ) : null}
          </div>

          {/* Name */}
          <button className="flex items-center gap-1 group">
            <span className="text-[15px] font-semibold text-white leading-tight">
              {displayName}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-[#5a5a5a] group-hover:text-[#fafafa] transition-colors" />
          </button>

          {/* Member count */}
          {(isChannel || isGroup) && (
            <button
              onClick={isGroup && canAddMembers ? onAddMembers : undefined}
              className="flex items-center gap-1 text-[11px] text-[#5a5a5a] hover:text-[#fafafa] transition-colors ml-1"
            >
              <Users className="h-3 w-3" />
              <span>{memberCount}</span>
            </button>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1">
          {isGroup && canAddMembers && (
            <HeaderActionBtn icon={Users} label="Add members" onClick={onAddMembers} />
          )}
        </div>
      </div>

      {/* Messages area */}
      {loadingMessages ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="h-5 w-5 rounded-full border-2 border-[#2e2e2e] border-t-[#8a8a8a] animate-spin" />
        </div>
      ) : (
        <div ref={threadRef} className="flex-1 overflow-y-auto py-2">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 select-none px-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-[#1e1e1e] flex items-center justify-center">
                {isChannel ? (
                  <Hash className="h-5 w-5 text-[#5a5a5a]" />
                ) : (
                  <MessageCircle className="h-5 w-5 text-[#5a5a5a]" />
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#8a8a8a]">No messages yet</p>
                <p className="text-[11px] text-[#5a5a5a] mt-0.5">
                  Be the first to start the conversation.
                </p>
              </div>
            </div>
          ) : (
            groupByDate(messages).map((group) => (
              <div key={group.label}>
                <DateSeparator label={group.label} />
                {group.items.map((msg, idx) => (
                  <SlackMessage
                    key={msg.id}
                    message={msg}
                    prevMessage={idx > 0 ? group.items[idx - 1] : null}
                    isGroupOrChannel={isGroup || isChannel}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {/* Composer or read-only bar */}
      {isReadOnly ? (
        <div className="border-t border-[#2e2e2e] px-6 py-3 text-[11px] text-[#5a5a5a] text-center shrink-0 select-none bg-[#0a0a0a]">
          System channel &middot; Read-only
        </div>
      ) : (
        <Composer
          draft={draft}
          onDraftChange={onDraftChange}
          onSend={handleSend}
          convType={conversation.type}
          mentionItems={mentionItems}
        />
      )}
    </div>
  );
}

function HeaderActionBtn({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      className="h-8 w-8 flex items-center justify-center rounded-lg text-[#5a5a5a] hover:text-white hover:bg-[#1e1e1e] transition-colors"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
